import React, { useState } from 'react';
import { useNavigation } from '../context/NavigationContext';
import { OfficialPortfolioSection } from '../components/OfficialPortfolioSection';
import { MarketingContactSection } from '../components/home/MarketingContactSection';
import {
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Globe2,
  MousePointerClick,
  Smartphone,
  Search,
  Zap,
  TrendingUp,
  Target,
  BarChart3,
  Layers,
  ShieldCheck,
  Check,
  Clock,
  Compass,
} from 'lucide-react';

export const HomePage: React.FC = () => {
  const { navigate } = useNavigation();
  const [activeQueryIndex, setActiveQueryIndex] = useState(0);

  const sampleQueries = [
    { query: "Dalaman'da restoran öner", location: 'Muğla / Dalaman', score: '78/100', status: 'İyi Düzeyde Öneriliyor', sources: 'Google Maps + Tripadvisor' },
    { query: "Dalaman'da kahvaltı nerede yapılır?", location: 'Muğla / Dalaman', score: '84/100', status: 'Öncelikli Listeleniyor', sources: 'Yerel İşletme Profili + Menü' },
    { query: "Muğla'da yat kiralama", location: 'Göcek / Fethiye', score: '91/100', status: 'En Üst Segmentte', sources: 'Tekne Filosu Kataloğu + Web' },
    { query: "Dalaman'da oto servis", location: 'Dalaman Sanayi', score: '65/100', status: 'Geliştirilebilir Profil', sources: 'Çalışma Saatleri + Konum' },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 selection:bg-blue-600 selection:text-white overflow-x-hidden">
      {/* ========================================================================= */}
      {/* SECTION 1: HERO (Clear in 5 seconds: 2 Core Pillars) */}
      {/* 1. Web sitesini kendim oluşturabilirim */}
      {/* 2. AI Local Visibility ile bölgemde bulunabilirliğimi ölçüp geliştirebilirim */}
      {/* ========================================================================= */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden">
        {/* Ambient Subtle Gradients */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bg-gradient-to-tr from-blue-600/15 via-indigo-600/10 to-transparent blur-[140px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Top Quiet Kicker (Zero-Pill Discipline) */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-400 border border-blue-500/20 mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Tek Merkezden Dijital Büyüme Platformu</span>
            </div>

            {/* Hero Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white font-sans leading-[1.12] mb-6">
              Dijital dünyada <br className="hidden sm:inline" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-300 to-sky-300">
                daha görünür olun.
              </span>
            </h1>

            {/* Hero Subheading */}
            <p className="text-base sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-10 font-normal">
              Web sitenizi dakikalar içinde oluşturun, yerel AI aramalarındaki görünürlüğünüzü test edin ve dijital varlığınızı tek merkezden büyütün.
            </p>

            {/* Primary & Secondary Action Group */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3.5 mb-12">
              <button
                onClick={() => navigate('/kayit')}
                className="w-full sm:w-auto px-7 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm shadow-xl shadow-blue-600/30 transition flex items-center justify-center gap-2 active:scale-95"
              >
                <span>Web Sitesi Oluştur</span>
                <ArrowRight className="w-4 h-4" />
              </button>

              <button
                onClick={() => {
                  const el = document.getElementById('ai-visibility');
                  el?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full sm:w-auto px-7 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-850 text-slate-200 hover:text-white font-semibold text-sm border border-slate-800 transition flex items-center justify-center gap-2"
              >
                <Search className="w-4 h-4 text-blue-400" />
                <span>AI Görünürlüğünü Test Et</span>
              </button>
            </div>

            {/* 4 Core Value Indicators */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-left max-w-3xl mx-auto pt-6 border-t border-slate-800/80">
              <div className="p-3 rounded-xl bg-slate-900/50 border border-slate-800/60">
                <span className="text-xs text-slate-400 block mb-1">01. Web Sitesi</span>
                <span className="text-xs sm:text-sm font-semibold text-white">Dakikalar İçinde Kurulum</span>
              </div>
              <div className="p-3 rounded-xl bg-slate-900/50 border border-slate-800/60">
                <span className="text-xs text-slate-400 block mb-1">02. Yerel AI</span>
                <span className="text-xs sm:text-sm font-semibold text-white">Arama Görünürlük Analizi</span>
              </div>
              <div className="p-3 rounded-xl bg-slate-900/50 border border-slate-800/60">
                <span className="text-xs text-slate-400 block mb-1">03. Görsel Editör</span>
                <span className="text-xs sm:text-sm font-semibold text-white">Sürükle & Bırak Yönetim</span>
              </div>
              <div className="p-3 rounded-xl bg-slate-900/50 border border-slate-800/60">
                <span className="text-xs text-slate-400 block mb-1">04. Altyapı</span>
                <span className="text-xs sm:text-sm font-semibold text-white">Özel Domain & Otomatik SSL</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* SECTION 2: 2 CORE PRODUCTS COMPARISON CARDS */}
      {/* 01 Website Builder | 02 AI Local Visibility */}
      {/* ========================================================================= */}
      <section className="py-20 bg-slate-900/40 border-y border-slate-800/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
              2 Temel Çözüm, Tek Entegre Platform
            </h2>
            <p className="text-xs sm:text-sm text-slate-400 mt-2">
              İşletmenizin dijital varlığını güçlendirmek ve yapay zekâ aramalarında öne çıkmak için gereken modern çözümler.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 max-w-5xl mx-auto gap-8">
            {/* Card 01: Website Builder */}
            <div className="p-8 rounded-3xl bg-slate-900/90 border border-slate-800 flex flex-col justify-between hover:border-slate-700 transition group shadow-xl">
              <div>
                <div className="flex items-center justify-between mb-6">
                  <span className="text-xs font-mono text-blue-400 font-bold tracking-widest">01</span>
                  <div className="w-10 h-10 rounded-xl bg-blue-600/10 border border-blue-500/20 text-blue-400 flex items-center justify-center">
                    <Globe2 className="w-5 h-5" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">WEBSITE BUILDER</h3>
                <p className="text-sm font-semibold text-blue-300 mb-3">
                  &quot;Profesyonel sitenizi oluşturun.&quot;
                </p>
                <p className="text-xs text-slate-400 leading-relaxed mb-6">
                  Görsel sürükle-bırak editör, mobil uyumlu sektörel bloklar, kendi alan adınız ve hazır formlarla saatlerce beklemeden web sitenizi hazırlayın.
                </p>
              </div>

              <div className="pt-4 border-t border-slate-800/80 flex items-center justify-between">
                <button
                  onClick={() => navigate('/kayit')}
                  className="w-full py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold text-xs transition flex items-center justify-center gap-1.5"
                >
                  <span>Site Oluştur</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Card 02: AI Local Visibility */}
            <div className="p-8 rounded-3xl bg-slate-900/90 border border-slate-800 flex flex-col justify-between hover:border-slate-700 transition group shadow-xl">
              <div>
                <div className="flex items-center justify-between mb-6">
                  <span className="text-xs font-mono text-indigo-400 font-bold tracking-widest">02</span>
                  <div className="w-10 h-10 rounded-xl bg-indigo-600/10 border border-indigo-500/20 text-indigo-400 flex items-center justify-center">
                    <Search className="w-5 h-5" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">AI LOCAL VISIBILITY</h3>
                <p className="text-sm font-semibold text-indigo-300 mb-3">
                  &quot;Yerel AI aramalarındaki görünürlüğünüzü test edin.&quot;
                </p>
                <p className="text-xs text-slate-400 leading-relaxed mb-6">
                  Bölgenizdeki yapay zekâ ve harita sorgularında işletmenizin nasıl yanıtlandığını ölçün, eksik verileri tespit edin ve uygulanabilir öneriler alın.
                </p>
              </div>

              <div className="pt-4 border-t border-slate-800/80 flex items-center justify-between">
                <button
                  onClick={() => {
                    const el = document.getElementById('ai-visibility');
                    el?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="w-full py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-xs transition flex items-center justify-center gap-1.5"
                >
                  <span>Görünürlüğü İncele</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* SECTION 3: WEBSITE BUILDER SECTION */}
      {/* "Web sitenizi saatlerce beklemeden oluşturun." */}
      {/* Realistic promises: "dakikalar içinde hazırlayın", "değişiklikleri anında önizleyin" */}
      {/* ========================================================================= */}
      <section className="py-24 sm:py-32 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Product Narrative */}
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-400 border border-blue-500/20">
                <Globe2 className="w-3.5 h-3.5" />
                <span>Web Sitesi Oluşturucu</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
                Web sitenizi saatlerce <br className="hidden sm:inline" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-sky-300">
                  beklemeden oluşturun.
                </span>
              </h2>

              <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                Web sitenizi dakikalar içinde hazırlayın, değişiklikleri anında önizleyin ve yayınlama sürecini birkaç basit adımda tamamlayın.
              </p>

              {/* Real Feature Checklist */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="flex items-center gap-2 text-xs text-slate-200">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>AI ile özgün sayfa kurgusu</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-200">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Canlı görsel editör</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-200">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>%100 mobil uyumlu tasarım</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-200">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Kendi alan adınızla yayın (.com, .com.tr)</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-200">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Otomatik SSL güvenlik sertifikası</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-200">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Dahili SEO ve meta etiketleri</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-200">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>İletişim & rezervasyon formları</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-200">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Güvenli bulut altyapısı barındırma</span>
                </div>
              </div>

              <div className="pt-4 flex flex-wrap items-center gap-4">
                <button
                  onClick={() => navigate('/kayit')}
                  className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs sm:text-sm shadow-lg shadow-blue-600/30 transition flex items-center gap-2 active:scale-95"
                >
                  <span>Hemen Site Oluştur</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate('/fiyatlandirma')}
                  className="px-5 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white font-semibold text-xs sm:text-sm border border-slate-800 transition"
                >
                  Paketleri İncele
                </button>
              </div>
            </div>

            {/* Right Column: Visual Editor Flow UI */}
            <div className="lg:col-span-6">
              <div className="p-4 sm:p-6 rounded-3xl bg-slate-900/90 border border-slate-800 shadow-2xl space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-slate-800 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                    <span className="text-slate-400 font-mono ml-2 text-[11px]">Adiosoft Görsel Editör</span>
                  </div>
                  <span className="text-[11px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                    Canlı Önizleme
                  </span>
                </div>

                {/* 4 Steps Timeline Flow */}
                <div className="space-y-3">
                  <div className="p-3 rounded-xl bg-slate-950/80 border border-slate-800 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-lg bg-blue-600/20 text-blue-400 text-xs font-bold flex items-center justify-center">1</span>
                      <span className="text-xs font-semibold text-white">Sektörünüzü & Şablonu Belirleyin</span>
                    </div>
                    <span className="text-[10px] text-slate-400">10+ Hazır Sektör</span>
                  </div>

                  <div className="p-3 rounded-xl bg-slate-950/80 border border-slate-800 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-lg bg-indigo-600/20 text-indigo-400 text-xs font-bold flex items-center justify-center">2</span>
                      <span className="text-xs font-semibold text-white">Metin ve Görselleri Düzenleyin</span>
                    </div>
                    <span className="text-[10px] text-slate-400">Sürükle-Bırak</span>
                  </div>

                  <div className="p-3 rounded-xl bg-slate-950/80 border border-slate-800 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-lg bg-sky-600/20 text-sky-400 text-xs font-bold flex items-center justify-center">3</span>
                      <span className="text-xs font-semibold text-white">Özel Alan Adınızı Tanımlayın</span>
                    </div>
                    <span className="text-[10px] text-slate-400">DNS & SSL Otomatik</span>
                  </div>

                  <div className="p-3 rounded-xl bg-blue-950/30 border border-blue-500/30 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-lg bg-emerald-600/20 text-emerald-400 text-xs font-bold flex items-center justify-center">4</span>
                      <span className="text-xs font-semibold text-white">Tek Tıkla Yayına Alın</span>
                    </div>
                    <span className="text-[10px] text-emerald-400 font-semibold">Anında Canlı</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* SECTION 4: AI LOCAL VISIBILITY SECTION */}
      {/* "Yerel AI Görünürlüğünüzü Ölçün" */}
      {/* Sample Queries: Dalaman'da restoran öner, Dalaman'da kahvaltı, Muğla'da yat, Dalaman'da oto servis */}
      {/* NO FALSE GUARANTEES: "Görünürlük ölçümü ve optimizasyon önerileri sunar" */}
      {/* ========================================================================= */}
      <section id="ai-visibility" className="py-24 sm:py-32 bg-slate-900/40 border-t border-slate-800/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left: Explanatory Content */}
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                <Search className="w-3.5 h-3.5" />
                <span>AI Local Visibility</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
                Yerel AI Görünürlüğünüzü <br className="hidden sm:inline" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-sky-300">
                  Ölçün ve Geliştirin.
                </span>
              </h2>

              <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                Yerel AI aramalarında nerede göründüğünüzü ölçün ve görünürlüğünüzü geliştirmek için uygulanabilir öneriler alın.
              </p>

              <div className="space-y-3 pt-2 text-xs sm:text-sm text-slate-300">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-indigo-600/20 text-indigo-400 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3" />
                  </div>
                  <span>AI aramalarında işletmenizin görünürlüğünü test eder.</span>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-indigo-600/20 text-indigo-400 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3" />
                  </div>
                  <span>Gerçek arama sonuçlarından kaynakları ve referansları inceler.</span>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-indigo-600/20 text-indigo-400 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3" />
                  </div>
                  <span>Web sitenizin teknik görünürlük altyapısını analiz eder.</span>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-indigo-600/20 text-indigo-400 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3" />
                  </div>
                  <span>Yerel işletme bilgilerindeki eksikleri tespit ederek somut adımlar önerir.</span>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 text-xs text-slate-400 leading-relaxed">
                <span className="text-amber-400 font-semibold block mb-1">Şeffaf Bilgilendirme:</span>
                AI Local Visibility aracı görünürlük ölçümü ve optimizasyon önerileri sunar; belirli bir yapay zekâ modelinde veya arama motorunda kesin 1. sıra garantisi vermez.
              </div>

              <div className="pt-2">
                <button
                  onClick={() => navigate('/kayit')}
                  className="px-6 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs sm:text-sm shadow-lg shadow-indigo-600/30 transition flex items-center gap-2 active:scale-95"
                >
                  <span>İşletmenizi Test Edin</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Right: Interactive Sample Query Visual with "Örnek görünüm" tag */}
            <div className="lg:col-span-6">
              <div className="p-6 rounded-3xl bg-slate-900/90 border border-slate-800 shadow-2xl space-y-5">
                <div className="flex items-center justify-between pb-3 border-b border-slate-800">
                  <div className="flex items-center gap-2 text-xs font-semibold text-white">
                    <Compass className="w-4 h-4 text-indigo-400" />
                    <span>AI Görünürlük Analiz Simülasyonu</span>
                  </div>
                  <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">
                    Örnek görünüm
                  </span>
                </div>

                {/* Sample Query Switchers */}
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-slate-400">Test Edilen Örnek Sorgular:</label>
                  <div className="grid grid-cols-2 gap-2">
                    {sampleQueries.map((item, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveQueryIndex(idx)}
                        className={`p-2.5 rounded-xl text-left text-xs transition border ${
                          activeQueryIndex === idx
                            ? 'bg-indigo-600/20 border-indigo-500/40 text-white font-semibold'
                            : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-slate-200'
                        }`}
                      >
                        <span className="line-clamp-1">&quot;{item.query}&quot;</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Analysis Result Card */}
                <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-400 font-mono text-[11px]">Sorgu: &quot;{sampleQueries[activeQueryIndex].query}&quot;</span>
                    <span className="text-emerald-400 font-bold">{sampleQueries[activeQueryIndex].score}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-xs pt-1">
                    <div className="p-2.5 rounded-lg bg-slate-900 border border-slate-800/80">
                      <span className="text-[10px] text-slate-500 block">AI Görünürlük Durumu</span>
                      <span className="text-white font-semibold">{sampleQueries[activeQueryIndex].status}</span>
                    </div>
                    <div className="p-2.5 rounded-lg bg-slate-900 border border-slate-800/80">
                      <span className="text-[10px] text-slate-500 block">Taranan Kaynaklar</span>
                      <span className="text-white font-semibold">{sampleQueries[activeQueryIndex].sources}</span>
                    </div>
                  </div>

                  <div className="text-[11px] text-slate-400 pt-1 border-t border-slate-800/60 leading-relaxed">
                    İşletmenizin yerel AI analiz raporu taranan kaynaklar üzerinden derlenir ve iyileştirme adımları sunulur.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* SECTION 5: REAL PORTFOLIO (Official projects from database, no fakes) */}
      {/* ========================================================================= */}
      <OfficialPortfolioSection />

      {/* ========================================================================= */}
      {/* SECTION 7: SHORT HIGH-CONVERTING CTA BANNER */}
      {/* ========================================================================= */}
      <section className="py-20 bg-gradient-to-b from-slate-900/60 to-slate-950 border-t border-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-4">
            İşletmenizin Dijital Vitrinini Bugün Oluşturun
          </h2>
          <p className="text-sm text-slate-300 max-w-xl mx-auto mb-8 leading-relaxed">
            Şablonunuzu belirleyin, içeriğinizi düzenleyin ve kendi alan adınızla yayına alın.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/kayit')}
              className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm shadow-xl shadow-blue-600/30 transition flex items-center justify-center gap-2 active:scale-95"
            >
              <span>Hemen Başlayın</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/fiyatlandirma')}
              className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-200 font-semibold text-sm border border-slate-800 transition"
            >
              Paket Detayları
            </button>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* SECTION 8: BİZE ULAŞIN (SINGLE WORKING INSTANCE, POST /api/marketing/contact) */}
      {/* ========================================================================= */}
      <MarketingContactSection />
    </div>
  );
};
export default HomePage;
